/**
 * ProfileSettingsSection — Name (editable) + Email (read-only).
 * Web port of client-native ProfileSettingsScreen.tsx. Saves via putName
 * (already in @/api/Api.ts) and invalidates userInfo.
 */
import { FC, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { colors } from "@/ui";
import { callWithAuth, putName } from "@/api/Api";
import { useSingleToast } from "@/hooks/useSingleToast";

interface ProfileSettingsSectionProps {
  firstName: string;
  email: string;
}

const ProfileSettingsSection: FC<ProfileSettingsSectionProps> = ({
  firstName,
  email,
}) => {
  const queryClient = useQueryClient();
  const toast = useSingleToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(firstName);

  const mutation = useMutation({
    mutationFn: (newName: string) =>
      callWithAuth(putName, { name: newName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userInfo"] });
      setEditing(false);
    },
    onError: () => {
      // iOS Alert.alert("Error", …); web surfaces a toast and stays in edit
      // mode so the user can retry without losing what they typed.
      toast({
        title: "Failed to update name",
        description: "Please try again later",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    },
  });

  // iOS saveNameAndClose: persist only when the name actually changed (avoids a
  // redundant putName + invalidation); always close if unchanged. Saves on blur
  // AND on Enter (iOS has no Save button — it commits on blur/submit).
  const saveAndClose = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== firstName) mutation.mutate(trimmed);
    else setEditing(false);
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
  } as const;

  return (
    <div>
      <div style={rowStyle}>
        <span className="text-base" style={{ color: colors.primary }}>Name</span>
        {editing ? (
          <input
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onBlur={saveAndClose}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            className="text-base"
            style={{
              textAlign: "right",
              border: `1px solid ${colors.greyLight}`,
              borderRadius: 8,
              padding: "4px 8px",
              outline: "none",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setName(firstName);
              setEditing(true);
            }}
            className="text-base"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: colors.secondary,
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            {/* iOS shows the value + a pencil glyph (not a chevron); empty = "Not set". */}
            {firstName || "Not set"}
            <Pencil size={16} color={colors.muted} />
          </button>
        )}
      </div>
      <div style={{ height: 1, backgroundColor: colors.greyLightest }} />
      <div style={rowStyle}>
        <span className="text-base" style={{ color: colors.primary }}>Email</span>
        <span
          className="text-base"
          style={{
            color: colors.secondary,
            maxWidth: "60%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={email}
        >
          {email}
        </span>
      </div>
    </div>
  );
};

export default ProfileSettingsSection;
